import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getStories } from "../api/apiHandler";
import { webStories as fallbackStories } from "../pages/home/homeData";
import { getMediaBaseUrl } from "../utils/backendUrl";

const StoryCard = ({ story }) => {
  const latest = story.slides[story.slides.length - 1];
  return (
    <Link
      to={`/web-stories/${story.id}`}
      className="rounded-2xl p-[3px] bg-gradient-to-tr from-[#4600a3] via-purple-600 to-purple-400 hover:scale-[1.03] transition-transform duration-300 cursor-pointer shadow-xl group block"
    >
      <div className="w-full aspect-[3/4] md:aspect-[9/16] rounded-xl overflow-hidden relative border-[3px] border-white bg-brand-red">
        {/* Image */}
        <img
          src={latest.image || story.image}
          alt={latest.heading}
          className="w-full h-full object-cover group-hover:scale-110 transition duration-700 ease-out opacity-90 group-hover:opacity-100"
        />

        {/* Dark Gradient Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent"></div>

        {/* Category Badge */}
        <div className="absolute top-3 left-3 bg-purple-700/90 backdrop-blur-sm text-white text-[10px] md:text-xs font-bold px-2 py-1 rounded shadow-sm">
          {story.category}
        </div>

        {/* Text Content */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-white text-xs md:text-[13px] font-bold leading-snug line-clamp-2 drop-shadow-lg group-hover:text-purple-300 transition-colors">
            {latest.heading || story.title}
          </h3>
          <div className="mt-2 text-gray-200 text-[11px] md:text-xs font-semibold flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all translate-y-3 group-hover:translate-y-0 duration-300">
            स्टोरी देखें <i className="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>
    </Link>
  );
};

const WebStoriesPage = () => {
  const [webStories, setWebStories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [itemsPerView, setItemsPerView] = useState(
    typeof window !== "undefined" && window.innerWidth >= 1024 ? 4 : 2
  );

  const BASE_URL = getMediaBaseUrl();

  const resolveImage = (filename) => {
    if (!filename) return "";
    if (filename.startsWith("http")) return filename;
    if (filename.startsWith("/images/")) return filename;
    return `${BASE_URL}/uploads/stories/${encodeURIComponent(filename)}`;
  };

  const mapStory = (item) => {
    const slides = [];
    if (item.image) {
      slides.push({ image: resolveImage(item.image), heading: item.heading || "" });
    }
    const extraImages = Array.isArray(item.images) ? item.images : [];
    const extraHeadings = Array.isArray(item.image_headings) ? item.image_headings : [];
    extraImages.forEach((img, i) => {
      slides.push({
        image: resolveImage(img),
        heading: extraHeadings[i] || "",
      });
    });
    return {
      id: item.id,
      category: item.category || "",
      slides: slides.length > 0 ? slides : [{ image: "", heading: item.heading || "" }],
    };
  };

  useEffect(() => {
    let isMounted = true;
    const loadStories = async () => {
      try {
        const res = await getStories();
        const items = Array.isArray(res?.data) ? res.data : [];
        if (isMounted) {
          const mapped = items.map(mapStory);
          setWebStories(mapped.length > 0 ? mapped : fallbackStories);
        }
      } catch (err) {
        console.error("Error fetching web stories:", err);
        if (isMounted) {
          setWebStories(fallbackStories);
        }
      }
    };
    loadStories();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSlider = webStories.length > 4;
  const maxIndex = Math.max(0, webStories.length - itemsPerView);

  useEffect(() => {
    const handleResize = () => setItemsPerView(window.innerWidth >= 1024 ? 4 : 2);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    if (!isSlider || isPaused || maxIndex === 0) return undefined;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 3000);
    return () => clearInterval(timer);
  }, [isSlider, isPaused, maxIndex]);

  const goToPrev = () =>
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));

  const goToNext = () =>
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));

  return (
    <div className="w-full">
      {!isSlider ? (
        /* ================= BEAUTIFUL 4-CARD GRID ================= */
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {webStories.slice(0, 4).map((story, index) => (
            <StoryCard key={story.id || index} story={story} />
          ))}
        </div>
      ) : (
        /* ================= AUTO SLIDER (WHEN MORE THAN 4 STORIES) ================= */
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="overflow-hidden -mx-3">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
              }}
            >
              {webStories.map((story, index) => (
                <div
                  key={story.id || index}
                  className="shrink-0 px-3"
                  style={{ width: `${100 / itemsPerView}%` }}
                >
                  <StoryCard story={story} />
                </div>
              ))}
            </div>
          </div>

          {/* LEFT ARROW */}
          <button
            type="button"
            onClick={goToPrev}
            aria-label="पिछली स्टोरी"
            className="absolute left-1 md:-left-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/95 backdrop-blur-sm text-brand-purple shadow-lg border border-gray-200 hover:bg-brand-purple hover:text-white hover:border-brand-purple transition-all duration-300 active:scale-95"
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>

          {/* RIGHT ARROW */}
          <button
            type="button"
            onClick={goToNext}
            aria-label="अगली स्टोरी"
            className="absolute right-1 md:-right-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/95 backdrop-blur-sm text-brand-purple shadow-lg border border-gray-200 hover:bg-brand-purple hover:text-white hover:border-brand-purple transition-all duration-300 active:scale-95"
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default WebStoriesPage;
